import type { AppUserData, DataRowStore } from "@glide/common-core/dist/js/components/datastore/data-row-store";
import {
    type ComputationModel,
    type LoadedGroundValue,
    type LoadingValue,
    type ResolvedGroundValue,
    type Row,
    makeLoadingValue,
    Table,
    type TableKeeper,
    type TableKeeperStore,
    mapLoadingValue,
    type Handler,
    type RowIndex,
    type SerializedQuery,
    Query,
} from "@glide/computation-model-types";
import {
    type ActionAppEnvironment,
    type ActionAppFacilities,
    type ActionOperationsState,
    type ActionOutstandingOperationsHandler,
    type AddRowToTableResult,
    type DataStoreMutationOptions,
    type GetOverrideRowID,
    type MutationResult,
    type OnQuerySaveHandler,
    type QueryableDataStore,
    type QueryableRowsRootFinder,
    emptyActionOperationsState,
} from "@glide/common-core/dist/js/components/types";
import { isTable } from "@glide/common-core/dist/js/computation-model/data";
import { extractActionValues } from "@glide/common-core/dist/js/computation-model/row-data";
import {
    getNativeTableRowIDForRowIndex,
    getRowIDForRowIndex,
    getRowIndexForRow,
} from "@glide/common-core/dist/js/computation-model/row-index";
import {
    type TableName,
    areTableNamesEqual,
    makeTableNameForNativeTable,
    nativeTableRowIDColumnName,
    type TableGlideType,
    type TypeSchema,
    findTable,
    getEmailOwnersColumnNames,
    getTableColumn,
    isPrimitiveType,
    isTableWritable,
    makeTableName,
    sheetNameForTable,
    tableGlideTypeCodecTableToTableGlideType,
    type NativeTableID,
    getTableName,
    makeNativeTableID,
} from "@glide/type-schema";
import {
    type BuilderQueryInfo,
    type ContinueQueryRequestBody,
    type ExecuteQueryRequestBody,
    type ExecuteQueryResponseBody,
    type NativeTableQueryID,
    type QueryContinuation,
    type QueryResponseEntry,
    type SaveQueryRequestBody,
    type SQLQueryBase,
    type WriteSourceType,
    executeQueryResponseBodyCodec,
} from "@glide/common-core/dist/js/firebase-function-types";
import { findTableNameForQuery, getQueryIDForTable } from "@glide/common-core/dist/js/queryable-table";
import { ShouldAgnostifyDateTimes } from "@glide/common-core/dist/js/schema-properties";
import { decodeTuple64Strings, encodeTuple64Strings } from "@glide/common-core/dist/js/support/tuple64";
import {
    type ChangeObservable,
    type JSONObject,
    ArrayMap,
    CombinedChangeObservable,
    concatIterableIterators,
    DefaultArrayMap,
    getResponseErrorMessage,
    ignore,
    isResponseOK,
    logError,
    nativeTableIndexer,
    SyncJobQueue,
    Watchable,
    deepEqual,
} from "@glide/support";
import {
    type ActionManagerAcessors,
    type ActionPoster,
    ActionManager,
    adaptValuesForWriting,
    BatchingActionPoster,
    debounceDataEditorSetColumnsTimeout,
    debounceSetColumnsTimeout,
    MultiplexingActionPoster,
    SimpleActionPoster,
} from "@glide/post-action";
import { evaluateQueryForRows } from "@glide/query-conditions";
import {
    assert,
    DefaultMap,
    defined,
    definedMap,
    exceptionToString,
    mapFilterUndefined,
    panic,
    sleep,
} from "@glideapps/ts-necessities";
import { continuePreviewQueryDataSource, previewQueryDataSource, saveQuery } from "@glide/backend-api";
import { setUnionInto } from "collection-utils";
import pLimit from "p-limit";
import { ComputationModelDataRowStoreBase } from "./data-row-store";
import { isRowOwnedByUser } from "./is-row-owned";
import { getDeviceID } from "@glide/common-core/dist/js/device-id";
import { type QueryRunnerCallbacks } from "./query/active-query";
import type { RunningQueryFreshener, StartQueryFreshener } from "./query/query-refresher";
import { TestLocalDataStoreImpl } from "./local-datastore";
import type { SimpleTableKeeper } from "./simple-table-keeper";
import type {
    ExistingQueryRowDataStore,
    ExistingQueryRowDataStoreCallbacks,
    MakeExistingQueryRowDataStore,
    QueryableBackendCaller,
} from "./queryable-data-store-types";
import { ExistingQueryRowDataStoreImpl, isExistingQueryRowDataStore } from "./query-row-data-store";
import { makeRowFromRowData, makeRowIDForRow } from "./queryable-data-store-support";
import { ErrorInfo, type ErrorOr } from "@glide/error-info";

const queryTablePrefix = "query-";
const previewQueryTablePrefix = queryTablePrefix + "preview";
const previewQueryNewPrefix = previewQueryTablePrefix + "-new-";
const previewQueryExistingPrefix = previewQueryTablePrefix + "-existing-";
const continuationWaitTime = 333; // Up to 3 calls per second, likely less with network latency.
const previewQueryLimit = 1000;

export function previewQueryTableNameForNew(sourceKind: string, sourceID: string): TableName {
    const savedName = `${previewQueryNewPrefix}${encodeTuple64Strings([sourceKind, sourceID])}`;
    return {
        name: savedName,
        isSpecial: true,
    };
}

function sourceKindIDFromPreviewQueryTableNameForNew(
    tableName: TableName
): { sourceID: string; sourceKind: string } | undefined {
    if (!tableName.name.startsWith(previewQueryNewPrefix)) return undefined;
    const t64Strings = tableName.name.substring(previewQueryNewPrefix.length);
    try {
        const decoded = decodeTuple64Strings(t64Strings);
        if (decoded.length !== 2) return undefined;
        return { sourceKind: decoded[0], sourceID: decoded[1] };
    } catch {
        return undefined;
    }
}

export function previewQueryTableNameForExisting(queryID: string): TableName {
    return {
        name: `${previewQueryExistingPrefix}${queryID}`,
        isSpecial: true,
    };
}

function queryIDFromPreviewQueryTableNameForExisting(tableName: TableName): NativeTableID | undefined {
    if (!tableName.name.startsWith(previewQueryExistingPrefix)) return undefined;
    return makeNativeTableID(tableName.name.substring(previewQueryExistingPrefix.length));
}

// FIXME: Do we ever have to unset `this.dataIsLoading`?
// FIXME: Get rid of this class and merge it into `PreviewQueryRowDataStoreBase`
abstract class QueryRowDataStoreBase<TQuery extends { queryName: string }, TBefore = any>
    extends ComputationModelDataRowStoreBase
    implements DataRowStore, Handler, TableKeeper
{
    private _currentTable: TableGlideType | undefined;
    protected _needsAnotherFetch: boolean = true;
    protected _currentQuery: TQuery | undefined;

    private readonly _fallbackTable: TableGlideType;

    constructor(
        protected readonly _appEnvironment: ActionAppEnvironment,
        protected readonly _tableName: TableName,
        displayName: string,
        private readonly updateTable: (newTable: TableGlideType) => void,
        protected readonly _queryRunning: Watchable<boolean>,
        // For debugging only
        private readonly _forBuilder: boolean
    ) {
        super(false, false);
        _queryRunning.current = false;
        this._fallbackTable = {
            name: _tableName,
            sheetName: displayName,
            columns: [],
            isReadOnly: true,
            emailOwnersColumn: undefined,
        };

        ignore(this._forBuilder);
    }

    public get symbolicRepresentation(): string {
        return `query-row-data-store ${this._tableName.name}`;
    }

    public readonly errorMessage = new Watchable<string | undefined>(undefined);
    public readonly warningMessage = new Watchable<string | undefined>(undefined);

    public getTable(): TableGlideType {
        if (this._currentTable === undefined) return this._fallbackTable;
        return this._currentTable;
    }

    protected getAppUserData(): AppUserData | undefined {
        return undefined;
    }

    protected setErrorMessage(message: string | undefined) {
        this.errorMessage.current = message;
    }

    protected setWarningMessage(message: string | undefined) {
        this.warningMessage.current = message;
    }

    public clear(): void {
        return panic("We shouldn't be cleared");
    }

    protected installNewTable(newTable: TableGlideType) {
        this._currentTable = newTable;
        this.updateTable(this._currentTable);
    }

    protected abstract runFirstQuery(
        queryID: string,
        querySpecs: TQuery,
        signal: AbortSignal
    ): Promise<QueryResponseEntry | undefined>;

    protected abstract runContinuationQuery(
        queryID: string,
        querySpecs: TQuery,
        continuation: unknown,
        signal: AbortSignal
    ): Promise<readonly QueryResponseEntry[] | undefined>;

    protected abstract onQuerySuccess(cookie: TBefore): void;
    protected abstract onQueryEnd(): void;

    // This will not set `_needsAnotherFetch`
    protected clearAll(): void {
        super.clear();
        this.tableData.clear();
        this.pushDirtForTable();
    }

    protected abstract fetchData(): void;
    public abstract abortOngoingQuery(): void;
    public abstract runQuery(subQuery: SerializedQuery, callback: () => void): Table | LoadingValue | undefined;
}

interface PreviewQuery {
    queryName: string;
    queryString: string;
    onBehalfOfUser: string | undefined;
}

abstract class PreviewQueryRowDataStoreBase extends QueryRowDataStoreBase<PreviewQuery, number> {
    private editSerial: number = 0;
    private savedSerial: number = -1;
    private runSerial: number = -1;
    protected setRemoteSaveSerial?(remoteSaveSerial: number): void;

    private _ongoingQueryAbortController: AbortController | undefined;
    private _queryQueue = new SyncJobQueue();

    constructor(
        appEnvironment: ActionAppEnvironment,
        tableName: TableName,
        public readonly appOwner: string | undefined,
        updatePreviewTable: (newPreviewTable: TableGlideType) => void,
        queryRunning: Watchable<boolean>,
        private readonly _continuationWaitTime: number
    ) {
        super(appEnvironment, tableName, "Preview Query", updatePreviewTable, queryRunning, true);
    }

    public currentQuerySaved(): boolean {
        return this.savedSerial === this.editSerial;
    }

    public canSaveCurrentQuery(): boolean {
        return this.runSerial === this.editSerial;
    }

    public canSaveCurrentQueryObservable = new Watchable<boolean>(false);

    public setQueryText(
        queryName: string | undefined,
        queryString: string | undefined,
        remoteSerial: number | undefined
    ): void {
        if (queryString === undefined) {
            this._currentQuery = undefined;
            this.editSerial++;
        } else if (queryName !== this._currentQuery?.queryName || queryString !== this._currentQuery?.queryString) {
            this._currentQuery = {
                onBehalfOfUser: this.appOwner,
                queryName: queryName ?? "Untitled Query",
                queryString,
            };
            this.editSerial++;
            if (remoteSerial !== undefined) {
                this.setRemoteSaveSerial?.(remoteSerial);
            }
        }
        this.canSaveCurrentQueryObservable.current = this.canSaveCurrentQuery();
    }

    public clearQuery() {
        this._currentQuery = undefined;
        this.editSerial++;
        if (this._queryRunning.current === true) return;

        this.clearAll();
        this.installNewTable({ name: this._tableName, columns: [], emailOwnersColumn: undefined });
        this.canSaveCurrentQueryObservable.current = this.canSaveCurrentQuery();
    }

    protected onQuerySuccess(cookie: number): void {
        this.runSerial = Math.max(this.runSerial, cookie);
        this.canSaveCurrentQueryObservable.current = this.canSaveCurrentQuery();
    }

    protected onQueryEnd(): void {
        if (this._currentQuery === undefined) {
            this.clearAll();
            this.installNewTable({ name: this._tableName, columns: [], emailOwnersColumn: undefined });
            this.canSaveCurrentQueryObservable.current = this.canSaveCurrentQuery();
        }
    }

    protected abstract getQuerySaveID(): SaveQueryRequestBody["savedIdentity"];

    private async saveQueryInternal(
        current: typeof this._currentQuery,
        editSerial: number,
        resultingTable: TableGlideType,
        onSaveHandler: OnQuerySaveHandler | undefined
    ): Promise<TableGlideType | undefined> {
        if (current === undefined) return undefined;
        if (this.appOwner === undefined) return undefined;
        const { queryName, queryString } = current;

        this.setErrorMessage(undefined);
        this.setWarningMessage(undefined);

        try {
            const resp = await saveQuery(
                this.getQuerySaveID(),
                this._appEnvironment.appID,
                this.appOwner,
                queryName,
                { kind: "query", query: queryString },
                resultingTable,
                this._appEnvironment.appFacilities
            );
            if (typeof resp === "string") {
                this.setErrorMessage(resp);
                return undefined;
            }

            const {
                savedIdentity: { currentSerial: remoteSerial, nativeTableID },
                requestWasSaved,
            } = resp;
            this.savedSerial = editSerial;
            this.setRemoteSaveSerial?.(remoteSerial);
            this.canSaveCurrentQueryObservable.current = this.canSaveCurrentQuery();
            const resultTable = {
                ...resultingTable,
                name: makeTableNameForNativeTable(nativeTableID),
            };
            if (requestWasSaved) {
                await onSaveHandler?.(resultTable, nativeTableID, remoteSerial);
                return resultTable;
            } else {
                this.setErrorMessage("Query was not saved due to contents conflict");
                return undefined;
            }
        } catch (e: unknown) {
            logError("Exception when trying to save query", e);
            this.setErrorMessage(`Encountered error when trying to save query: ${exceptionToString(e)}`);
            return undefined;
        }
    }

    private saveQueue = new SyncJobQueue();
    public isSaving = new Watchable<boolean>(false);

    public async saveCurrentQuery(
        getOverrideRowID: GetOverrideRowID,
        handler: OnQuerySaveHandler | undefined
    ): Promise<TableGlideType | undefined> {
        if (!this.canSaveCurrentQuery()) return;

        const current = this._currentQuery;
        const { editSerial } = this;

        let resultingTable = this.getTable();

        const rowIDOverride = await getOverrideRowID(resultingTable);
        if (rowIDOverride === undefined) return undefined;

        if (rowIDOverride !== resultingTable.rowIDColumn) {
            const column = getTableColumn(resultingTable, rowIDOverride);
            assert(column !== undefined && isPrimitiveType(column.type));
            resultingTable = {
                ...resultingTable,
                rowIDColumn: rowIDOverride,
            };
            this.installNewTable(resultingTable);
        }

        if (resultingTable.rowIDColumn === undefined) {
            return;
        }

        const savePromise = this.saveQueue.run(() =>
            this.saveQueryInternal(current, editSerial, resultingTable, handler)
        );
        this.isSaving.current = this.saveQueue.hasOutstandingWork();
        try {
            return await savePromise;
        } finally {
            this.isSaving.current = this.saveQueue.hasOutstandingWork();
        }
    }

    protected async runFirstQuery(
        queryID: string,
        querySpecs: PreviewQuery,
        _signal: AbortSignal
    ): Promise<QueryResponseEntry | undefined> {
        // FIXME: Actually use the AbortSignal here.

        const { onBehalfOfUser: onBehalfOf, queryName, queryString } = querySpecs;
        const savedIdentity = this.getQuerySaveID();

        const resp = await previewQueryDataSource(
            savedIdentity,
            onBehalfOf,
            queryName,
            queryID,
            { kind: "query", query: queryString },
            previewQueryLimit,
            this._appEnvironment.appFacilities
        );
        if (typeof resp === "string") {
            this.setErrorMessage(resp);
            return undefined;
        }
        return resp;
    }

    protected async runContinuationQuery(
        queryID: string,
        querySpecs: PreviewQuery,
        continuation: QueryContinuation,
        _signal: AbortSignal
    ): Promise<readonly QueryResponseEntry[] | undefined> {
        // FIXME: Actually use the AbortSignal here.

        const { onBehalfOfUser: onBehalfOf } = querySpecs;
        const savedIdentity = this.getQuerySaveID();

        const result = await continuePreviewQueryDataSource(
            this._appEnvironment.appFacilities,
            savedIdentity,
            onBehalfOf,
            queryID,
            continuation
        );
        if (typeof result === "string") {
            this.setWarningMessage(result);
            return undefined;
        }

        return result;
    }

    private async runQueryInQueueInternal(callbacks: QueryRunnerCallbacks): Promise<void> {
        let numRowsAdded = 0;
        function addMaybeRows(maybeRows: readonly unknown[]) {
            const rows: JSONObject[] = [];
            for (const row of maybeRows) {
                if (row === null || typeof row !== "object") continue;
                rows.push(row as JSONObject);
            }
            if (rows.length === 0) return false;
            callbacks.addRows(rows);
            numRowsAdded += rows.length;
            return true;
        }

        const cookie = this.editSerial;
        const firstResponse = await callbacks.runFirstQuery();
        if (firstResponse === undefined) return;

        if (firstResponse.kind === "error") {
            logError("First query attempt error:", firstResponse);
            this.setErrorMessage(firstResponse.message);
            return;
        }

        let currentContinuation = firstResponse.continuation;
        let needsClearing = true;
        if (firstResponse.rows.length > 0 || currentContinuation === undefined) {
            this.clearAll();
            needsClearing = false;
            callbacks.installNewTable(firstResponse.table);
        }
        if (addMaybeRows(firstResponse.rows)) {
            this.pushDirtForTable();
        }
        let loggedError = false;
        while (currentContinuation !== undefined && numRowsAdded < 1000) {
            // We don't need to saturate the network just running continuations.
            await sleep(this._continuationWaitTime);
            const startContinuation = currentContinuation;
            const nextResponse = await callbacks.runContinuationQuery(startContinuation);
            if (nextResponse === undefined) break;

            const queryResult = nextResponse.find(r => r.queryID === callbacks.subQueryID);
            if (queryResult === undefined) {
                logError("Continuation attempt didn't return the right query ID", nextResponse);
                this.setErrorMessage("Invalid response from backend");
                return;
            }

            if (queryResult.kind === "error") {
                logError("Continuation attempt error:", queryResult);
                if (!loggedError) {
                    this.setErrorMessage(queryResult.message);
                    loggedError = true;
                }
                if (currentContinuation === startContinuation) {
                    currentContinuation = undefined;
                }
            } else {
                currentContinuation = queryResult.continuation;
                if (needsClearing) {
                    this.clearAll();
                    needsClearing = false;
                }
                callbacks.installNewTable(queryResult.table);
                if (addMaybeRows(queryResult.rows)) {
                    this.pushDirtForTable();
                }
            }
        }
        if (!loggedError && cookie !== undefined) {
            this.onQuerySuccess(cookie);
        }
    }

    private async runQueryInQueue(query: PreviewQuery, signal: AbortSignal): Promise<void> {
        try {
            this.setErrorMessage(undefined);
            this.setWarningMessage(undefined);
            this._needsAnotherFetch = false;
            this._queryRunning.current = true;

            const subQueryID = this._appEnvironment.appFacilities.makeUUID();

            let rowIndex = nativeTableIndexer.zero;
            await this.runQueryInQueueInternal({
                subQueryID,
                addRows: rows => {
                    const table = this.getTable();
                    for (const rowData of rows ?? []) {
                        const rowID = makeRowIDForRow(table, rowData, this._appEnvironment.appFacilities);
                        this.addRow(makeRowFromRowData(table, rowID, rowData, rowIndex));
                        rowIndex = nativeTableIndexer.nextNumber(rowIndex);
                    }
                    return true;
                },
                installNewTable: t =>
                    this.installNewTable({
                        emailOwnersColumn: undefined,
                        ...t,
                        name: this._tableName,
                        sheetName: query.queryName,
                    }),
                runFirstQuery: async () => {
                    const response = await this.runFirstQuery(subQueryID, query, signal);
                    if (signal.aborted) return undefined;
                    return response;
                },
                runContinuationQuery: cont => this.runContinuationQuery(subQueryID, query, cont, signal),
            });
        } finally {
            this.setDirty();
            this._queryRunning.current = false;
            this.onQueryEnd();
        }
    }

    protected fetchData(): void {
        if (!this._needsAnotherFetch || this._currentQuery === undefined) return;
        this.abortOngoingQuery();
        const controller = new AbortController();
        this._ongoingQueryAbortController = controller;
        const query = this._currentQuery;

        void this._queryQueue.run(() => this.runQueryInQueue(query, controller.signal));
    }

    public abortOngoingQuery(): void {
        if (this._ongoingQueryAbortController !== undefined) {
            this._ongoingQueryAbortController.abort();
        }
    }

    public runQuery() {
        this._needsAnotherFetch = true;
        this.fetchData();
        return undefined;
    }
}

// Exported for testing
export class PreviewNewQueryRowDataStore extends PreviewQueryRowDataStoreBase {
    constructor(
        appEnvironment: ActionAppEnvironment,
        private readonly _sourceKind: string,
        private readonly _sourceID: string,
        onBehalfOf: string | undefined,
        updatePreviewTable: (newPreviewTable: TableGlideType) => void,
        queryRunning: Watchable<boolean>,
        waitTime: number = continuationWaitTime
    ) {
        super(
            appEnvironment,
            previewQueryTableNameForNew(_sourceKind, _sourceID),
            onBehalfOf,
            updatePreviewTable,
            queryRunning,
            waitTime
        );
    }

    protected getQuerySaveID() {
        return {
            kind: "new" as const,
            sourceKind: this._sourceKind,
            sourceID: this._sourceID,
        };
    }
}

class PreviewExistingQueryRowDataStore extends PreviewQueryRowDataStoreBase {
    private _remoteSerial: number | undefined;
    private readonly _queryID: NativeTableQueryID;

    constructor(
        appEnvironment: ActionAppEnvironment,
        queryID: string,
        onBehalfOf: string | undefined,
        updatePreviewTable: (newPreviewTable: TableGlideType) => void,
        queryRunning: Watchable<boolean>
    ) {
        super(
            appEnvironment,
            previewQueryTableNameForExisting(queryID),
            onBehalfOf,
            updatePreviewTable,
            queryRunning,
            continuationWaitTime
        );
        this._queryID = {
            kind: "native-table",
            value: makeNativeTableID(queryID),
        };
    }

    protected getQuerySaveID() {
        return {
            kind: "existing" as const,
            id: this._queryID,
            lastSerial: this._remoteSerial ?? -1,
        };
    }

    protected setRemoteSaveSerial(remoteSerial: number) {
        this._remoteSerial = remoteSerial;
    }
}

// exported only for testing
export class QueryableBackendCallerImpl implements QueryableBackendCaller {
    // We limit the number of concurrent `executeQuery` requests on the
    // frontend.  If we ever run into allocating too many promises here, or if
    // we want to abort queries, we will have to the
    // `ConcurrencyLimitedWithBackpressure`.
    private readonly limit = pLimit(5);

    constructor(private readonly deviceID: string) {}

    private async parseResponse(response: Response | undefined): Promise<ErrorOr<ExecuteQueryResponseBody>> {
        if (response === undefined) return new ErrorInfo(500, "Could not contact the servers to run the query.");

        let error: ErrorInfo | undefined;
        try {
            const responseBody = await response.json();
            if (executeQueryResponseBodyCodec.is(responseBody)) {
                return responseBody;
            }

            if (isResponseOK(response)) {
                error = new ErrorInfo(400, "Unexpected response from the server");
            } else {
                const message = await getResponseErrorMessage(response);
                error = new ErrorInfo(response.status, message ?? response.statusText);
            }
        } catch {
            if (!isResponseOK(response)) {
                error = new ErrorInfo(response.status, response.statusText);
            }
        }

        if (error === undefined) {
            error = new ErrorInfo(500, "Unknown error executing query");
        }
        logError("Error in response", error.message);
        return error;
    }

    private async executeQuery(
        appFacilities: ActionAppFacilities,
        body: ExecuteQueryRequestBody
    ): Promise<ErrorOr<ExecuteQueryResponseBody>> {
        const response = await appFacilities.callAuthIfAvailableCloudFunction("executeQuery", body, {}, true);
        return this.parseResponse(response);
    }

    public async requestExecuteQuery(
        appEnv: ActionAppEnvironment,
        savedID: NativeTableQueryID,
        builderInfo: true | BuilderQueryInfo | undefined,
        requests: ExecuteQueryRequestBody["requests"]
    ): Promise<ErrorOr<ExecuteQueryResponseBody>> {
        const { appFacilities, appID } = appEnv;
        const body: ExecuteQueryRequestBody = { appID, savedID, requests, builderInfo, deviceID: this.deviceID };

        return await this.limit(() => this.executeQuery(appFacilities, body));
    }

    public async requestContinueQuery(
        appEnv: ActionAppEnvironment,
        savedID: NativeTableQueryID,
        builderInfo: true | BuilderQueryInfo | undefined,
        requests: ContinueQueryRequestBody["requests"]
    ): Promise<ErrorOr<ExecuteQueryResponseBody>> {
        const { appFacilities, appID } = appEnv;

        const body: ContinueQueryRequestBody = {
            appID,
            savedID,
            requests,
            builderInfo,
            deviceID: this.deviceID,
        };
        const response = await appFacilities.callAuthIfAvailableCloudFunction("continueQuery", body, {}, true);
        return this.parseResponse(response);
    }
}

interface Overrides {
    readonly actionPoster: ActionPoster;
    readonly simpleActionPoster: ActionPoster;
    readonly batchingActionPoster: BatchingActionPoster;
    readonly makeExistingQueryRowDataStore: MakeExistingQueryRowDataStore;
    readonly debounceTimeout: number;
}

interface Options {
    readonly forceInitialQueryToPrimary: boolean;
    readonly shortCircuitRowIDQueries: boolean;
}

export class QueryableDataStoreImpl
    implements QueryableDataStore, QueryableRowsRootFinder, ExistingQueryRowDataStoreCallbacks
{
    private readonly _queryableRowsRootFinders = new Set<QueryableRowsRootFinder>();
    // These hold the total number of rows in this query, including all the
    // ones we haven't loaded yet.
    private readonly _numRowsForTable = new DefaultArrayMap<TableName, Watchable<number | undefined>>(
        areTableNamesEqual,
        () => new Watchable<number | undefined>(undefined)
    );
    private readonly _locallyModifiedRows = new DefaultArrayMap<TableName, Set<string>>(
        areTableNamesEqual,
        () => new Set()
    );
    private readonly _backendCaller: QueryableBackendCaller;
    private readonly _simpleActionPoster: ActionPoster;
    private _batchingActionPoster: BatchingActionPoster | undefined;
    private _actionPoster: ActionPoster | undefined;
    private _actionManager: ActionManager | undefined;
    private _outstandingOperationsHandlers = new Set<ActionOutstandingOperationsHandler>();

    private readonly _makeExistingQueryRowDataStore: MakeExistingQueryRowDataStore;

    private _appUserDataObservable: ChangeObservable<AppUserData> | undefined;

    // We're using a global row index fallback across all tables, and across
    // the builder and player data stores.  This is technically not super
    // nice, but we're only using the row index fallback for row ordering, and
    // we don't compare rows between the builder and player.  Even if we were
    // more meticulous here we'd end up with inconsistent row indexes because
    // the builder and player run different queries, so the same row can be
    // returned at different times.
    private _nextRowIndexFallback: string = nativeTableIndexer.zero;

    private readonly _queryFreshener: RunningQueryFreshener;

    constructor(
        private readonly _appEnvironment: ActionAppEnvironment,
        public readonly isBuilder: boolean,
        public readonly writeSource: WriteSourceType,
        private readonly deviceIDOverride: string | undefined,
        backendCallerOverride: QueryableBackendCaller | undefined,
        private readonly appOwnerID: string | undefined,
        private readonly startQueryFreshener: StartQueryFreshener,
        private readonly accessors: ActionManagerAcessors,
        private readonly options: Options,
        private readonly overrides?: Partial<Overrides>
    ) {
        this._simpleActionPoster = overrides?.simpleActionPoster ?? new SimpleActionPoster(this.accessors);
        this._actionPoster = overrides?.actionPoster;
        this._batchingActionPoster = overrides?.batchingActionPoster;
        this._makeExistingQueryRowDataStore =
            overrides?.makeExistingQueryRowDataStore ?? ((...args) => new ExistingQueryRowDataStoreImpl(...args));
        this._backendCaller =
            backendCallerOverride ?? new QueryableBackendCallerImpl(deviceIDOverride ?? getDeviceID());

        this._queryFreshener = this.startQueryFreshener(
            2_000,
            () => this.allTables(),
            () => this._appEnvironment
        );
    }

    public allTables() {
        return concatIterableIterators(this.playerExistingTables.values(), this.builderExistingTables.values());
    }

    public subscribeToOutstandingOperations(handler: ActionOutstandingOperationsHandler): void {
        if (this._actionManager !== undefined) {
            this._actionManager.subscribeToOutstandingOperations(handler);
            assert(this._outstandingOperationsHandlers.size === 0);
        } else if (!this._outstandingOperationsHandlers.has(handler)) {
            this._outstandingOperationsHandlers.add(handler);
            handler(this.getActionOutstandingOperations());
        }
    }

    public unsubscribeFromOutstandingOperations(handler: ActionOutstandingOperationsHandler): void {
        if (this._actionManager !== undefined) {
            this._actionManager.unsubscribeFromOutstandingOperations(handler);
        } else {
            this._outstandingOperationsHandlers.delete(handler);
        }
    }

    public getActionOutstandingOperations(): ActionOperationsState {
        if (this._actionManager !== undefined) {
            return this._actionManager.getActionOutstandingOperations();
        }
        return emptyActionOperationsState;
    }

    private getExistingDataRowStoresForTable(tableName: TableName): readonly ExistingQueryRowDataStore[] {
        return mapFilterUndefined([false, true], forBuilder => {
            const target = this.getDataRowStoreForTable(tableName, forBuilder);
            if (isExistingQueryRowDataStore(target)) {
                return target;
            } else {
                return undefined;
            }
        });
    }

    private get batchingActionPoster(): BatchingActionPoster {
        if (this._batchingActionPoster === undefined) {
            this._batchingActionPoster = new BatchingActionPoster(this._appEnvironment.appFacilities, false);
        }

        return this._batchingActionPoster;
    }

    // This is `public` only for testing
    public getActionPosterForTable(tableName: TableName): ActionPoster {
        const table = this.findTable(tableName);
        if (table === undefined) return this._simpleActionPoster;

        return this.batchingActionPoster;
    }

    private getActionManager(): ActionManager {
        if (this._actionManager === undefined) {
            if (this._actionPoster === undefined) {
                this._actionPoster = new MultiplexingActionPoster(tn => this.getActionPosterForTable(tn));
            }
            this._actionManager = new ActionManager(
                this.accessors,
                this.deviceIDOverride,
                this.overrides?.debounceTimeout ?? debounceSetColumnsTimeout,
                this.overrides?.debounceTimeout ?? debounceDataEditorSetColumnsTimeout,
                this.isBuilder,
                this.writeSource,
                "-queryable",
                this._actionPoster,
                async (tableName, rowIndex, jobID, version) => {
                    const table = this.findTable(tableName);
                    if (table === undefined) return;

                    const rowID = getRowIDForRowIndex(table, rowIndex);
                    if (rowID === undefined) return;

                    for (const target of this.getExistingDataRowStoresForTable(tableName)) {
                        target.confirmAction(rowID, jobID, version);
                    }
                },
                async (tableName, rowIndex, jobID) => {
                    const table = this.findTable(tableName);
                    if (table === undefined) return;

                    const rowID = getRowIDForRowIndex(table, rowIndex);
                    if (rowID === undefined) return;

                    for (const target of this.getExistingDataRowStoresForTable(tableName)) {
                        target.rollbackAction(rowID, jobID);
                    }
                },
                (tableName, jobIDA, jobIDB) => {
                    for (const target of this.getExistingDataRowStoresForTable(tableName)) {
                        target.obsoleteAction(jobIDA, jobIDB);
                    }
                }
            );

            const handler = Array.from(this._outstandingOperationsHandlers);
            this._outstandingOperationsHandlers.clear();
            for (const h of handler) {
                this._actionManager.subscribeToOutstandingOperations(h);
            }
        } else {
            assert(this._outstandingOperationsHandlers.size === 0);
        }
        return this._actionManager;
    }

    private _queryRunningStates = new DefaultMap<string, Watchable<boolean>>(() => new Watchable<boolean>(false));
    private _schema: TypeSchema | undefined;

    public get schema(): TypeSchema {
        return panic("The schema should come from the firestore datastore");
    }

    private _previewSchemaSerial = new Watchable(0);

    // FIXME: do we need this anymore?
    public get previewSchemaSerial(): ChangeObservable<number> {
        return this._previewSchemaSerial;
    }

    public getComputationModelObservable(_forBuilder: boolean): ChangeObservable<ComputationModel | undefined> {
        return panic("Get the computation model from the main data store");
    }

    public fetchTableRows(_tableName: TableName, _forBuilder: boolean): void {
        // nothing to do
    }

    public getFirstRowObservable(
        tableName: TableName,
        isBuilder: boolean
    ): ChangeObservable<Row | undefined> | undefined {
        return this.getDataRowStoreForTable(tableName, isBuilder)?.firstRowObservable;
    }

    private updateTable = (newTable: TableGlideType) => {
        const { name: maybeTableName } = newTable;
        const tableName = makeTableName(maybeTableName);
        if (tableName.name.startsWith(previewQueryTablePrefix)) {
            this._previewSchemaSerial.current += 1;
        }
    };

    public getQueryRunningFlag(tableName: TableName): Watchable<boolean> | undefined {
        if (!tableName.name.startsWith(queryTablePrefix)) return undefined;
        return this._queryRunningStates.get(tableName.name);
    }

    private previewTablesForSources = new DefaultMap<string, DefaultMap<string, PreviewNewQueryRowDataStore>>(
        sourceKind =>
            new DefaultMap<string, PreviewNewQueryRowDataStore>(sourceID => {
                const internalTableName = previewQueryTableNameForNew(sourceKind, sourceID);
                const queryRunningFlag = defined(this.getQueryRunningFlag(internalTableName));
                const dataStore = new PreviewNewQueryRowDataStore(
                    this._appEnvironment,
                    sourceKind,
                    sourceID,
                    this.appOwnerID,
                    this.updateTable,
                    queryRunningFlag
                );
                return dataStore;
            })
    );

    public getPreviewTableForNew(sourceKind: string, sourceID: string): TableGlideType {
        return this.previewTablesForSources.get(sourceKind).get(sourceID).getTable();
    }

    private previewTablesForExisting = new DefaultMap<NativeTableID, PreviewExistingQueryRowDataStore>(queryID => {
        const internalTableName = previewQueryTableNameForExisting(queryID);
        const queryRunningFlag = defined(this.getQueryRunningFlag(internalTableName));
        const dataStore = new PreviewExistingQueryRowDataStore(
            this._appEnvironment,
            queryID,
            this.appOwnerID,
            this.updateTable,
            queryRunningFlag
        );
        return dataStore;
    });

    public getPreviewTableForExisting(queryID: NativeTableID): TableGlideType {
        return this.previewTablesForExisting.get(queryID).getTable();
    }

    private readonly existingTableDisplayNames = new Map<string, string>();

    private readonly playerQueriesRunningObservable = new CombinedChangeObservable<boolean, boolean>(a =>
        a.some(x => x)
    );
    private readonly builderQueriesRunningObservable = new CombinedChangeObservable<boolean, boolean>(a =>
        a.some(x => x)
    );

    private getQueriesRunningObservable(forBuilder: boolean): CombinedChangeObservable<boolean, boolean> {
        return forBuilder ? this.builderQueriesRunningObservable : this.playerQueriesRunningObservable;
    }

    private getBuilderInfo(forBuilder: boolean): true | BuilderQueryInfo | undefined {
        return (
            forBuilder ||
            definedMap(this._appUserDataObservable?.current, appUserData => ({
                previewAsEmail: appUserData.email ?? "",
                previewAsRoles: Array.from(appUserData.roles),
            }))
        );
    }

    private makeExistingTablesMap(
        forBuilder: boolean
    ): DefaultMap<NativeTableID, ExistingQueryRowDataStore | undefined> {
        return new DefaultMap(queryID => {
            if (forBuilder && !this.isBuilder) return undefined;

            const tableName = findTableNameForQuery(this._appEnvironment.sourceMetadata, this._schema, queryID);
            // FIXME: When we return `undefined` here then it's forever.  Instead we
            // should try again the next time.
            if (tableName === undefined) return undefined;
            const table = definedMap(this._schema, s => findTable(s, tableName));
            const dataStore = this._makeExistingQueryRowDataStore(
                this._appEnvironment,
                tableName,
                table,
                { kind: "native-table", value: makeNativeTableID(queryID) },
                this.options.forceInitialQueryToPrimary ? "latest" : undefined,
                this._queryFreshener.buildQueryRefresher,
                this._backendCaller,
                this,
                this.options.shortCircuitRowIDQueries
            );
            dataStore.setBuilderInfo(this.getBuilderInfo(forBuilder));
            this.getQueriesRunningObservable(forBuilder).addInput(dataStore.areSubscribedQueriesRunning);
            return dataStore;
        });
    }

    private readonly playerExistingTables = this.makeExistingTablesMap(false);
    private readonly builderExistingTables = this.makeExistingTablesMap(true);

    public appEnvironmentUpdated(): void {
        this.getActionManager().updateOfflineQueue();
        this.getActionManager().resetReloadResiliencyQueue();
    }

    private appUserChanged = (): void => {
        this.getActionManager().updateOfflineQueue();
        this.getActionManager().resetReloadResiliencyQueue();

        const schema = this._schema;
        if (schema === undefined) return;

        for (const table of schema.tables) {
            const queryID = this.getQueryIDForTable(table);
            if (queryID === undefined) continue;

            const hasRowOwners = getEmailOwnersColumnNames(table).length > 0;
            const hasUserSpecific = table.columns.some(c => c.isUserSpecific === true);

            if (hasUserSpecific || hasRowOwners) {
                this.resetQueryByID(queryID, undefined, true);
            }
        }
    };

    private findTable(tableName: TableName): TableGlideType | undefined {
        return definedMap(this._schema, s => findTable(s, tableName));
    }

    public async addRowToTable(
        { tableName, sendToBackend, setUnderlyingData, awaitSend, onError, ...metadata }: DataStoreMutationOptions,
        columnValues: Record<string, LoadedGroundValue>,
        columnNames: ReadonlySet<string> | undefined
    ): Promise<AddRowToTableResult> {
        const table = this.findTable(tableName);
        if (table === undefined || !isTableWritable(table)) {
            return {
                didAdd: false,
                playerRow: undefined,
                builderRow: undefined,
                jobID: undefined,
                confirmedAtVersion: undefined,
            };
        }

        const values = {
            // We allow `columnValues` to overwrite the row ID.  We need to
            // specify it out here, or otherwise `extractActionValues` might
            // remove it.
            $rowID: this._appEnvironment.appFacilities.makeRowID(),
            ...adaptValuesForWriting(
                table,
                this._appEnvironment.sourceMetadata ?? ShouldAgnostifyDateTimes.Keep,
                {
                    convertToString: false,
                    removeUnknownColumns: false,
                    allowRowID: true,
                    appUserEmail:
                        this._appEnvironment.authenticator.virtualEmail ?? this._appEnvironment.authenticator.realEmail,
                    fromDataEditor: metadata.fromDataEditor,
                },
                extractActionValues(columnValues, columnNames)
            ),
        };

        const jobID = this._appEnvironment.appFacilities.makeRowID();

        let playerRow: Row = { ...values, $isVisible: true };
        assert(typeof playerRow.$rowID === "string");

        const promise = sendToBackend
            ? this.getActionManager().addRowToTable(
                  tableName,
                  getRowIndexForRow(table, undefined, playerRow),
                  values,
                  jobID,
                  metadata,
                  onError,
                  true
              )
            : Promise.resolve(false);

        let confirmedAtVersion: number | undefined;
        // NOTE: We hardcode here the fact that external queryable data
        // sources (in particular SQL) might return a new row that's different
        // from the one we added.
        if (table.sourceMetadata?.externalSource !== undefined) {
            const result = await promise;
            if (result === false) {
                return { didAdd: false, playerRow: undefined, builderRow: undefined, jobID, confirmedAtVersion: false };
            }
            if (typeof result !== "boolean" && result.newRow !== undefined) {
                const rowData = {
                    // To make TS happy, and in case the backend didn't send
                    // us a row ID back.
                    $rowID: values.$rowID,
                    ...result.newRow,
                    $isVisible: true,
                };
                playerRow = makeRowFromRowData(
                    table,
                    makeRowIDForRow(table, rowData, this._appEnvironment.appFacilities),
                    rowData,
                    this.makeRowIndexFallback()
                );
            }
            confirmedAtVersion = typeof result !== "boolean" ? result.confirmedAtVersion : undefined;
        }

        // The player and builder rows must be distinct objects, or the
        // computation model will do weird things.
        const builderRow = { ...playerRow };

        if (setUnderlyingData) {
            for (const forBuilder of [false, true]) {
                const rowToAdd = forBuilder ? builderRow : playerRow;
                const target = this.getDataRowStoreForTable(tableName, forBuilder);
                if (isExistingQueryRowDataStore(target)) {
                    target.addRowFromAction(rowToAdd, jobID);
                    if (confirmedAtVersion !== undefined) {
                        // ##doubleAddRowActionConfirmation:
                        // The `ActionManager` will already have confirmed
                        // this action, but it will have done that up where we
                        // awaited `promise`, which is before we added the row
                        // to the data row store, which means it won't have
                        // done anything.  So here we do it again, this time
                        // knowing that the data row store knows about this
                        // job.  We could eliminate the superfluous
                        // confirmation, but it's not harmful, and it would
                        // require additional bookkeeping.
                        target.confirmAction(rowToAdd.$rowID, jobID, confirmedAtVersion);
                    }
                } else {
                    target?.addRow(rowToAdd);
                }
            }
            this._locallyModifiedRows.get(tableName).add(playerRow.$rowID);
        }

        let didAdd = true;
        if (awaitSend) {
            const result = await promise;
            // If this fails then we depend on the action rollback to remove
            // the row again.

            didAdd = result !== false;

            if (typeof result !== "boolean" && result.confirmedAtVersion !== undefined) {
                confirmedAtVersion = result.confirmedAtVersion;
            }
        }

        return { didAdd, playerRow, builderRow, jobID, confirmedAtVersion };
    }

    public async setColumnsInRow(
        { tableName, sendToBackend, setUnderlyingData, awaitSend, onError, ...metadata }: DataStoreMutationOptions,
        rowIndex: RowIndex,
        columnValues: Record<string, LoadedGroundValue>,
        withDebounce: boolean,
        _onCompletion: (() => void) | undefined,
        existingJobID: string | undefined,
        confirmedAtVersion: number | undefined
    ): Promise<MutationResult> {
        if (Object.keys(columnValues).length === 0) return { jobID: undefined, confirmedAtVersion: true };

        // This should have already been covered by the constructor, but if there's
        // a transient exception that got thrown we might be able to recover.
        this.getActionManager().processSetColumnsInRowResiliencyQueueWhenOnline();

        const table = this.findTable(tableName);
        if (table === undefined || !isTableWritable(table)) return { jobID: undefined, confirmedAtVersion: false };

        const values = adaptValuesForWriting(
            table,
            this._appEnvironment.sourceMetadata ?? ShouldAgnostifyDateTimes.Keep,
            {
                convertToString: false,
                removeUnknownColumns: false,
                allowRowID: false,
                fromDataEditor: metadata.fromDataEditor,
            },
            extractActionValues(columnValues, undefined)
        );

        const jobID = existingJobID ?? this._appEnvironment.appFacilities.makeRowID();

        if (setUnderlyingData) {
            const rowID = getNativeTableRowIDForRowIndex(rowIndex);
            for (const forBuilder of [false, true]) {
                const target = this.getDataRowStoreForTable(tableName, forBuilder);
                if (isExistingQueryRowDataStore(target)) {
                    target.setColumnsInRowFromAction(rowIndex, values, jobID);
                    if (confirmedAtVersion !== undefined) {
                        if (rowID !== undefined) {
                            target.confirmAction(rowID, jobID, confirmedAtVersion);
                        }
                    }
                } else {
                    // This shouldn't actually ever happen - only the
                    // `ExistingQueryRowDataStore` allows mutation.
                    target?.setColumnsInRow(rowIndex, values);
                }
            }
            if (rowID !== undefined) {
                this._locallyModifiedRows.get(tableName).add(rowID);
            }
        }

        if (confirmedAtVersion === undefined && sendToBackend) {
            return await this.getActionManager().setColumnsInRow(
                tableName,
                rowIndex,
                values,
                existingJobID !== undefined,
                jobID,
                withDebounce,
                metadata,
                onError,
                true
            );
        }

        return { jobID, confirmedAtVersion };
    }

    public async deleteRowsAtIndexes(
        { tableName, sendToBackend, setUnderlyingData, awaitSend, onError, ...metadata }: DataStoreMutationOptions,
        rowIndexes: readonly RowIndex[]
    ): Promise<MutationResult> {
        const table = this.findTable(tableName);
        if (table === undefined || !isTableWritable(table)) return { jobID: undefined, confirmedAtVersion: false };

        // This is a faux job ID we use to track confirmation.  We don't ever
        // actually create an action document with this ID.
        const jobID = this._appEnvironment.appFacilities.makeRowID();

        if (setUnderlyingData) {
            for (const forBuilder of [false, true]) {
                const target = this.getDataRowStoreForTable(tableName, forBuilder);
                for (const rowIndex of rowIndexes) {
                    if (isExistingQueryRowDataStore(target)) {
                        target.deleteRowAtIndexFromAction(rowIndex, jobID);
                    } else {
                        target?.deleteRowAtIndex(rowIndex);
                    }

                    // Make sure we only do this once
                    if (!forBuilder) {
                        const rowID = getNativeTableRowIDForRowIndex(rowIndex);
                        if (rowID !== undefined) {
                            this._locallyModifiedRows.get(tableName).add(rowID);
                        }
                    }
                }
            }
        }

        if (sendToBackend) {
            const promise = this.getActionManager().deleteRows(tableName, rowIndexes, metadata, jobID, onError, true);

            if (awaitSend) {
                return await promise;
            }
        }

        return { jobID, confirmedAtVersion: undefined };
    }

    public setEmailOwnersColumns(): void {
        // Not actually implemented.
    }

    public afterSetEmailOwnersColumns(tableName: TableName): void {
        this.resetQueryByTableName(tableName, undefined);
    }

    private getQueryIDForTable(table: TableGlideType): NativeTableID | undefined {
        return getQueryIDForTable(this._appEnvironment.sourceMetadata, table);
    }

    private getQueryIDForTableName(tableName: TableName): NativeTableID | undefined {
        const table = this.findTable(tableName);
        if (table === undefined) return undefined;
        return this.getQueryIDForTable(table);
    }

    public setSchema(schema: TypeSchema | undefined): void {
        this._schema = schema;

        const newTables = [...(schema?.tables ?? []).filter(t => this.getQueryIDForTable(t) !== undefined)];

        // We always preserve the preview tables for new queries.
        for (const sourceIDMap of this.previewTablesForSources.values()) {
            for (const dataStore of sourceIDMap.values()) {
                const table = dataStore.getTable();
                const tableName = makeTableName(table.name);
                const newSchemaIndex = newTables.findIndex(t => areTableNamesEqual(makeTableName(t.name), tableName));
                if (newSchemaIndex >= 0) {
                    newTables[newSchemaIndex] = table;
                } else {
                    newTables.push(table);
                }
            }
        }
        const retainQueryIDs = new Set(
            mapFilterUndefined(newTables, t => queryIDFromPreviewQueryTableNameForExisting(makeTableName(t.name)))
        );
        const removeQueryIDs = new Set<NativeTableID>();
        for (const [queryID, dataStore] of this.previewTablesForExisting.entries()) {
            if (!retainQueryIDs.has(queryID)) {
                removeQueryIDs.add(queryID);
                continue;
            }
            const table = dataStore.getTable();
            const tableName = makeTableName(table.name);
            const newSchemaIndex = newTables.findIndex(t => areTableNamesEqual(makeTableName(t.name), tableName));
            if (newSchemaIndex >= 0) {
                newTables[newSchemaIndex] = table;
            } else {
                newTables.push(table);
            }
        }
        for (const queryID of removeQueryIDs) {
            this.previewTablesForExisting.delete(queryID);
            this.existingTableDisplayNames.delete(queryID);
            this.playerExistingTables.delete(queryID);
            this.builderExistingTables.delete(queryID);
        }
        for (const table of newTables) {
            const maybeExistingTableID = this.getQueryIDForTable(table);
            if (maybeExistingTableID === undefined) continue;
            if (table.sourceMetadata?.type !== "Native table") continue;
            if (table.sourceMetadata.type === "Native table" && table.sourceMetadata.needsQuery !== true) continue;
            const displayName = sheetNameForTable(table);
            this.existingTableDisplayNames.set(maybeExistingTableID, displayName);

            for (const existing of [this.builderExistingTables, this.playerExistingTables]) {
                // `existing` is a `DefaultMap`, so this `has` check is
                // necessary.
                if (!existing.has(maybeExistingTableID)) continue;
                existing.get(maybeExistingTableID)?.setTable(table);
            }
        }
        this._previewSchemaSerial.current += 1;
    }

    public setAppUserDataObservable(observable: ChangeObservable<AppUserData>): void {
        if (this._appUserDataObservable === observable) return;

        this._appUserDataObservable?.unsubscribe(this.appUserChanged);
        this._appUserDataObservable = observable;
        this._appUserDataObservable.subscribe(this.appUserChanged);

        // We're assuming the app user has changed when the provider changes,
        // because we might already have queried with the wrong "builder
        // info".
        // https://github.com/quicktype/glide/issues/19242
        this.appUserChanged();
    }

    public userProfileTableChanged(): void {
        // Not actually implemented.
    }

    public isRowOwnedByUser(tableName: TableName, row: Row): boolean {
        const table = this.findTable(tableName);
        const appUserData = this._appUserDataObservable?.current;
        return isRowOwnedByUser(row, appUserData, table);
    }

    public resetFromUpstream(): void {
        // Actually implement this one.
    }

    public addRowOwnerChangeCallback(): void {
        // Not actually implemented.
    }

    public removeRowOwnerChangeCallback(): void {
        // Not actually implemented.
    }

    public retire(): void {
        this._appUserDataObservable?.unsubscribe(this.appUserChanged);
        this._queryFreshener.stop();
    }

    public getDataRowStoreForTable(tableName: TableName, forBuilder: boolean) {
        const maybePreviewSourceKindID = sourceKindIDFromPreviewQueryTableNameForNew(tableName);
        if (maybePreviewSourceKindID !== undefined) {
            const { sourceKind, sourceID } = maybePreviewSourceKindID;
            return this.previewTablesForSources.get(sourceKind).get(sourceID);
        }
        const maybePreviewExisting = queryIDFromPreviewQueryTableNameForExisting(tableName);
        if (maybePreviewExisting !== undefined) {
            return this.previewTablesForExisting.get(maybePreviewExisting);
        }
        const maybeExistingTableID = this.getQueryIDForTableName(tableName);
        if (maybeExistingTableID !== undefined) {
            if (forBuilder) {
                return this.builderExistingTables.get(maybeExistingTableID);
            } else {
                return this.playerExistingTables.get(maybeExistingTableID);
            }
        }
        return undefined;
    }

    public getPreviewQueryCanSaveFlagForNew(sourceKind: string, sourceID: string): ChangeObservable<boolean> {
        return this.previewTablesForSources.get(sourceKind).get(sourceID).canSaveCurrentQueryObservable;
    }

    public setPreviewQueryForNew(sourceKind: string, sourceID: string, queryName: string, queryString: string): void {
        this.previewTablesForSources.get(sourceKind).get(sourceID).setQueryText(queryName, queryString, undefined);
    }

    public clearPreviewQueryForNew(sourceKind: string, sourceID: string): void {
        this.previewTablesForSources.get(sourceKind).get(sourceID).clearQuery();
    }

    public async savePreviewQueryForNew(
        sourceKind: string,
        sourceID: string,
        getOverrideRowID: GetOverrideRowID,
        saveHandler?: OnQuerySaveHandler
    ): Promise<TableGlideType | undefined> {
        return await this.previewTablesForSources
            .get(sourceKind)
            .get(sourceID)
            .saveCurrentQuery(getOverrideRowID, saveHandler);
    }

    public getPreviewQueryForNewSavingFlag(sourceKind: string, sourceID: string): ChangeObservable<boolean> {
        return this.previewTablesForSources.get(sourceKind).get(sourceID).isSaving;
    }

    public getPreviewQueryCanSaveForExisting(queryID: NativeTableID): ChangeObservable<boolean> {
        return this.previewTablesForExisting.get(queryID).canSaveCurrentQueryObservable;
    }

    public setPreviewQueryForExisting(
        queryID: NativeTableID,
        queryName: string,
        queryString: string,
        remoteSerial?: number | undefined
    ): void {
        this.previewTablesForExisting.get(queryID).setQueryText(queryName, queryString, remoteSerial);
    }

    private reportSave(queryID: NativeTableID): void {
        if (this.builderExistingTables.has(queryID)) {
            const existingTable = this.builderExistingTables.get(queryID);
            existingTable?.reportSave();
        }
        if (this.playerExistingTables.has(queryID)) {
            const existingTable = this.playerExistingTables.get(queryID);
            existingTable?.reportSave();
        }
    }

    public async savePreviewQueryForExisting(
        queryID: NativeTableID,
        getOverrideRowID: GetOverrideRowID,
        saveHandler?: OnQuerySaveHandler
    ): Promise<TableGlideType | undefined> {
        const tableType = await this.previewTablesForExisting
            .get(queryID)
            .saveCurrentQuery(getOverrideRowID, saveHandler);
        if (tableType !== undefined) {
            this.reportSave(queryID);
        }
        return tableType;
    }

    public getPreviewQueryForExistingSavingFlag(queryID: NativeTableID): ChangeObservable<boolean> {
        return this.previewTablesForExisting.get(queryID).isSaving;
    }

    public getQueryErrorMessage(tableName: TableName): ChangeObservable<string | undefined> | undefined {
        const target = this.getDataRowStoreForTable(tableName, true);
        if (target === undefined || isExistingQueryRowDataStore(target)) return undefined;
        return target.errorMessage;
    }

    public getQueryWarningMessage(tableName: TableName): ChangeObservable<string | undefined> | undefined {
        const target = this.getDataRowStoreForTable(tableName, true);
        if (target === undefined || isExistingQueryRowDataStore(target)) return undefined;
        return target?.warningMessage;
    }

    // This is run on the frontend when the user does "reload" on a table in
    // the data editor.
    public async rediscoverQuery(
        tableName: TableName,
        queryName: string,
        queryBase: SQLQueryBase,
        remoteSerial: number,
        getOverrideRowID: GetOverrideRowID,
        retrySleep: number = continuationWaitTime
    ): Promise<string | undefined> {
        if (this.appOwnerID === undefined) return "Unknown error rediscovering query";

        const table = this.findTable(tableName);
        if (table === undefined) return "Table does not exist";

        const queryID = this.getQueryIDForTable(table);
        if (queryID === undefined) return "Table cannot be reloaded";

        // We "preview" the table
        const savedIdentity = {
            kind: "existing",
            id: {
                kind: "native-table",
                value: makeNativeTableID(queryID),
            },
            lastSerial: remoteSerial,
        } as const;
        const requestID = this._appEnvironment.appFacilities.makeUUID();
        const response = await previewQueryDataSource(
            savedIdentity,
            this.appOwnerID,
            queryName,
            requestID,
            queryBase,
            1,
            this._appEnvironment.appFacilities
        );
        if (typeof response === "string") return response;

        let previewTable: TableGlideType | undefined;
        if (response.rows.length > 0 || response.continuation === undefined) {
            previewTable = { emailOwnersColumn: undefined, ...response.table };
        } else {
            let continuation = response.continuation;
            for (;;) {
                await sleep(retrySleep);
                const entries = await continuePreviewQueryDataSource(
                    this._appEnvironment.appFacilities,
                    savedIdentity,
                    this.appOwnerID,
                    requestID,
                    continuation
                );
                if (typeof entries === "string") return entries;
                if (entries.length !== 1) return "Invalid response from backend";

                const [entry] = entries;
                if (entry.kind === "error") return entry.message;

                if (entry.rows.length > 0 || entry.continuation === undefined) {
                    previewTable = { emailOwnersColumn: undefined, ...entry.table };
                    break;
                }

                continuation = entry.continuation;
            }
        }

        // If the response's row ID column is the native table one that means
        // the data source has a primary key and we're using it.  Otherwise we
        // try to reuse the existing row ID column.
        let rowIDColumn =
            previewTable.rowIDColumn === nativeTableRowIDColumnName ? nativeTableRowIDColumnName : table.rowIDColumn;
        if (rowIDColumn === undefined || getTableColumn(previewTable.columns, rowIDColumn) === undefined) {
            // If we don't have a row ID column name, or there's no column by
            // that name, we invoke the primary key picker.
            rowIDColumn = await getOverrideRowID(previewTable);
            // If `rowIDColumn` is `undefined` then the user has canceled, so
            // we return `undefined` for success.
            if (rowIDColumn === undefined) return undefined;
        }

        // and then we call back to the backend to "save" the query
        await saveQuery(
            savedIdentity,
            this._appEnvironment.appID,
            this.appOwnerID,
            queryName,
            queryBase,
            {
                ...tableGlideTypeCodecTableToTableGlideType(previewTable),
                name: table.name,
                sheetName: table.sheetName,
                rowIDColumn,
            },
            this._appEnvironment.appFacilities
        );

        this.reportSave(queryID);

        return undefined;
    }

    // This is currently only called from the query editor when submitting a
    // query.
    //
    // FIXME: Get rid of this arbitrary limit here - would be nice to support
    // pagination.
    public runQuery(tableName: TableName): void {
        const target = this.getDataRowStoreForTable(tableName, true);
        if (target === undefined) return undefined;
        if (isExistingQueryRowDataStore(target)) {
            target.runQuery(new Query(tableName).withLimit(100).serialize(), () => undefined);
        } else {
            target.runQuery();
        }
    }

    public setNumberOfRowsInTable(tableName: TableName, numRows: number): void {
        this._numRowsForTable.get(tableName).current = numRows;
    }

    public makeRowIndexFallback(): string {
        const rowIndex = this._nextRowIndexFallback;
        this._nextRowIndexFallback = nativeTableIndexer.nextNumber(this._nextRowIndexFallback);
        return rowIndex;
    }

    public getNumberOfRowsInTable(tableName: TableName): ChangeObservable<number | undefined> | undefined {
        return this._numRowsForTable.get(tableName);
    }

    public getCanSaveUserSpecificColumns(): boolean {
        return this.accessors.appUserID() !== undefined;
    }

    public fetchQuery(
        query: Query,
        onChange: () => void,
        forBuilder: boolean
    ): ResolvedGroundValue | LoadingValue | undefined {
        const queryID = this.getQueryIDForTableName(query.tableName);
        if (queryID === undefined) return undefined;

        const dataRowStore = forBuilder
            ? this.builderExistingTables.get(queryID)
            : this.playerExistingTables.get(queryID);
        if (dataRowStore === undefined) return undefined;

        const serialized = query.serialize();

        const result = dataRowStore.runQuery(serialized, onChange);
        if (result === undefined) return result;

        return mapLoadingValue(result, t => {
            assert(isTable(t));
            return query.postProcess(t);
        });
    }

    public getLocallyModifiedRowIDs(tableName: TableName): ReadonlySet<string> {
        return this._locallyModifiedRows.get(tableName);
    }

    private resetQueryByID(queryID: NativeTableID, version: number | undefined, appUserChanged: boolean): void {
        // We always reset for builder and player, even though technically
        // it's not always necessary.  If we didn't do this we'd sometimes
        // have inconsistencies between them which might confuse users.
        for (const forBuilder of [false, true]) {
            const tables = forBuilder ? this.builderExistingTables : this.playerExistingTables;
            const query = tables.get(queryID);
            if (query !== undefined) {
                query.setBuilderInfo(this.getBuilderInfo(forBuilder));
                query.resetAllQueries(version, appUserChanged);
            }
        }
    }

    public resetQueryByTableName(tableName: TableName, version: number | undefined): void {
        const queryID = this.getQueryIDForTableName(tableName);
        if (queryID === undefined) return;

        this.resetQueryByID(queryID, version, false);
    }

    public addQueryableRowsRootFinder(finder: QueryableRowsRootFinder): void {
        this._queryableRowsRootFinders.add(finder);
    }

    public removeQueryableRowsRootFinder(finder: QueryableRowsRootFinder): void {
        this._queryableRowsRootFinders.delete(finder);
    }

    public getRowIDsForTable(tableName: TableName): ReadonlySet<string> {
        const result = new Set<string>();
        for (const finder of this._queryableRowsRootFinders) {
            const rowIDs = finder.getRowIDsForTable(tableName);
            setUnionInto(result, rowIDs);
        }
        return result;
    }

    public get isReadyForGC(): boolean {
        return Array.from(this._queryableRowsRootFinders).every(f => f.isReadyForGC);
    }

    public resetReadyForGC(): void {
        for (const f of this._queryableRowsRootFinders) {
            f.resetReadyForGC();
        }
    }

    public getAreSubscribedQueriesRunning(forBuilder: boolean): ChangeObservable<boolean> {
        return this.getQueriesRunningObservable(forBuilder);
    }

    public getAppUserID(): string | undefined {
        return undefined;
    }
}

export class QueryableLayeredTableKeeper implements TableKeeperStore<TableKeeper> {
    constructor(
        private readonly _queryableDataStore: QueryableDataStore | undefined,
        private readonly _origKeeper: TableKeeperStore<TableKeeper>,
        private readonly _forBuilder: boolean,
        private readonly _shouldQuery: (tn: TableName) => boolean
    ) {}

    public getTableKeeperForTable(tn: TableName): TableKeeper {
        if (!this._shouldQuery(tn)) return this._origKeeper.getTableKeeperForTable(tn);
        const fromQueryable = this._queryableDataStore?.getDataRowStoreForTable(tn, this._forBuilder);
        return fromQueryable ?? this._origKeeper.getTableKeeperForTable(tn);
    }
}

export class MockQueryableDataStore extends TestLocalDataStoreImpl implements QueryableDataStore {
    private areSubscribedQueriesRunning: ChangeObservable<boolean>;
    private queryCache: DefaultMap<string, ArrayMap<SerializedQuery, ResolvedGroundValue | LoadingValue>>;

    constructor(
        appID: string,
        private tables: ReadonlyArray<TableGlideType>,
        baseTableKeeperStore: TableKeeperStore<SimpleTableKeeper> | undefined
    ) {
        super(appID, false, baseTableKeeperStore);

        this.areSubscribedQueriesRunning = new Watchable(false);
        this.queryCache = new DefaultMap(() => new ArrayMap((a, b) => deepEqual(a, b)));
    }

    setAppUserDataObservable(_observable: ChangeObservable<AppUserData>): void {
        throw new Error("setAppUserDataObservable NOT IMPLEMENTED");
    }

    getDataRowStoreForTable(_tableName: TableName, _forBuilder: boolean): (DataRowStore & TableKeeper) | undefined {
        throw new Error("getDataRowStoreForTable NOT IMPLEMENTED");
    }

    fetchQuery(
        query: Query,
        onChange: () => void,
        _forBuilder: boolean
    ): ResolvedGroundValue | LoadingValue | undefined {
        const tableName = query.tableName.name;
        const queryCache = this.queryCache.get(tableName);
        const serializedQuery = query.serialize();

        if (queryCache.has(serializedQuery)) {
            return queryCache.get(serializedQuery);
        }

        const maybeTable = this.tables.find(t => getTableName(t).name === tableName);
        if (maybeTable === undefined) {
            // eslint-disable-next-line no-console
            console.error(`Could not find table ${tableName}`);
            return undefined;
        }

        const keeper = this.tableKeeperStore.getTableKeeperForTable(query.tableName) as SimpleTableKeeper;

        const allRows = keeper.table.asArray();
        const response = evaluateQueryForRows(
            serializedQuery,
            maybeTable.columns,
            allRows,
            (r, cn) => r[cn],
            undefined
        );
        if (response instanceof Error) {
            logError(`Evaluate query failed: ${response.message}`);
            return undefined;
        }

        const result = query.postProcess(new Table(response.rows as Row[]));
        setTimeout(() => {
            queryCache.set(serializedQuery, result);
            onChange();
        }, 500);

        return makeLoadingValue();
    }

    getLocallyModifiedRowIDs(): ReadonlySet<string> {
        return new Set();
    }

    resetQueryByTableName(_tableName: TableName, _version: number | undefined): void {
        throw new Error("resetQueryByTableName NOT IMPLEMENTED");
    }

    getAreSubscribedQueriesRunning(_forBuilder: boolean): ChangeObservable<boolean> {
        return this.areSubscribedQueriesRunning;
    }

    // Everything that follows is used by the builder only.
    readonly previewSchemaSerial: ChangeObservable<number> = new Watchable<number>(0);

    getNumberOfRowsInTable(_tableName: TableName): ChangeObservable<number | undefined> | undefined {
        throw new Error("getNumberOfRowsInTable NOT IMPLEMENTED");
    }

    getQueryRunningFlag(_tableName: TableName): ChangeObservable<boolean> | undefined {
        throw new Error("getQueryRunningFlag NOT IMPLEMENTED");
    }
    getQueryErrorMessage(_tableName: TableName): ChangeObservable<string | undefined> | undefined {
        throw new Error("getQueryErrorMessage NOT IMPLEMENTED");
    }
    getQueryWarningMessage(_tableName: TableName): ChangeObservable<string | undefined> | undefined {
        throw new Error("getQueryWarningMessage NOT IMPLEMENTED");
    }
    runQuery(_tableName: TableName): void {
        throw new Error("runQuery NOT IMPLEMENTED");
    }

    savePreviewQueryForNew(
        _sourceKind: string,
        _sourceID: string,
        _getOverrideRowID: GetOverrideRowID,
        _onSave?: OnQuerySaveHandler
    ): Promise<TableGlideType | undefined> {
        throw new Error("savePreviewQueryForNew NOT IMPLEMENTED");
    }

    getPreviewTableForNew(_sourceKind: string, _sourceID: string): TableGlideType {
        throw new Error("getPreviewTableForNew NOT IMPLEMENTED.");
    }
    getPreviewTableForExisting(_queryID: string): TableGlideType {
        throw new Error("getPreviewTableForExisting NOT IMPLEMENTED.");
    }

    getPreviewQueryCanSaveFlagForNew(_sourceKind: string, _sourceID: string): ChangeObservable<boolean> {
        throw new Error("getPreviewQueryCanSaveFlagForNew NOT IMPLEMENTED.");
    }
    setPreviewQueryForNew(_sourceKind: string, _sourceID: string, _queryName: string, _queryString: String): void {
        throw new Error("setPreviewQueryForNew NOT IMPLEMENTED.");
    }
    clearPreviewQueryForNew(_sourceKind: string, _sourceID: string): void {
        throw new Error("clearPreviewQueryForNew NOT IMPLEMENTED.");
    }
    getPreviewQueryForNewSavingFlag(_sourceKind: string, _sourceID: string): ChangeObservable<boolean> {
        throw new Error("getPreviewQueryForNewSavingFlag NOT IMPLEMENTED.");
    }

    getPreviewQueryCanSaveForExisting(_queryID: string): ChangeObservable<boolean> {
        throw new Error("getPreviewQueryCanSaveForExisting NOT IMPLEMENTED.");
    }
    setPreviewQueryForExisting(
        _queryID: string,
        _queryName: string,
        _queryString: string,
        _remoteSerial?: number
    ): void {
        throw new Error("setPreviewQueryForExisting NOT IMPLEMENTED.");
    }
    savePreviewQueryForExisting(
        _queryID: string,
        _getOverrideRowID: GetOverrideRowID,
        _onSave?: OnQuerySaveHandler
    ): Promise<TableGlideType | undefined> {
        throw new Error("savePreviewQueryForExisting NOT IMPLEMENTED.");
    }
    getPreviewQueryForExistingSavingFlag(_queryID: string): ChangeObservable<boolean> {
        throw new Error("getPreviewQueryForExistingSavingFlag NOT IMPLEMENTED.");
    }

    rediscoverQuery(
        _tableName: TableName,
        _queryName: string,
        _queryBase: SQLQueryBase,
        _remoteSerial: number
    ): Promise<string | undefined> {
        throw new Error("rediscoverQuery NOT IMPLEMENTED.");
    }

    addQueryableRowsRootFinder(_finder: QueryableRowsRootFinder): void {
        // in practice, the `finder` here is the WireBackend.
        return;
    }
    removeQueryableRowsRootFinder(_finder: QueryableRowsRootFinder): void {
        throw new Error("removeQueryableRowsRootFinder NOT IMPLEMENTED.");
    }
}
